import { AlarmOff, AlarmOn } from '@mui/icons-material';
import { type Theme, Typography, styled, useTheme } from '@mui/material';
import { green } from '@mui/material/colors';
import { type SystemStyleObject } from '@mui/system';
import { type MovieStatus, type ScreenType, type TvStatus } from '@tmdb-graphql-api/schema/types';
import { mapScreenTypeStatusToWatchlistCategory } from '#helpers/enumMappers.ts';
import { tt } from '#i18n/index.ts';
import { useStore } from '#store/useStore.ts';

const Container = styled('span')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
  },
  alignItems: 'center',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
}));

export type WatchlistBadgeProps = {
  compact?: boolean;
  id: string;
  status: MovieStatus | TvStatus;
  sx?: SystemStyleObject<Theme>;
  type: ScreenType;
};

export const WatchlistBadge = ({ compact, id, status, sx, type }: WatchlistBadgeProps) => {
  const theme = useTheme();
  const addWatchlistItem = useStore(store => store.addWatchlistItem);
  const watchlist = useStore(store => store.watchlist);
  const removeWatchlistItem = useStore(store => store.removeWatchlistItem);
  // Not using store method because want the raw watchlist to take
  // advantage of re-render when watchlist changes.
  const hasWatchlistItem = watchlist.find(entry => entry.id === id && entry.type === type);
  const Icon = hasWatchlistItem ? AlarmOn : AlarmOff;
  const watchlistCategory = mapScreenTypeStatusToWatchlistCategory(status);

  const handleClick = () => {
    if (hasWatchlistItem) {
      removeWatchlistItem({ category: watchlistCategory, id, type });
    } else {
      addWatchlistItem({ category: watchlistCategory, id, type });
    }
  };

  return (
    <Container
      aria-label={hasWatchlistItem ? tt('watchlistBadge.removeAriaLabel') : tt('watchlistBadge.addAriaLabel')}
      onClick={handleClick}
      sx={{
        ...sx,
        justifyContent: compact ? 'center' : undefined,
      }}
    >
      {compact ? undefined : (
        <Typography
          sx={{
            [theme.breakpoints.down('sm')]: {
              display: 'none',
            },
            color: theme.supportingTextPrimary,
            fontSize: '0.75rem',
            marginBottom: theme.spacing(0.2),
            textAlign: 'center',
          }}
        >
          {tt('categories.watchlist').toUpperCase()}
        </Typography>
      )}
      <Icon
        sx={{
          color: green[500],
          fontSize: '2.2rem',
        }}
      />
    </Container>
  );
};
